import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Link,
  Spinner,
  Stack,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  useApp,
  useArrayFromRecord,
  useAuth,
  UseCollectionArgs,
  usePracticeAccess,
} from '@hb/react'
import { adminRoles, getAppRole, SITE_ERRORS, SiteError, WithId } from '@hb/shared'
import { useMemo } from 'react'
import { useListQuery } from '../../../hooks/backend/useListQuery'
import { FloatingContainer } from '../../Containers'

const PAGE_SIZE = 25

const formatTimestamp = (timestamp: string) => {
  const parsed = new Date(timestamp)
  if (Number.isNaN(parsed.getTime())) return timestamp
  return parsed.toLocaleString()
}

const SiteErrorCard = ({ siteError }: { siteError: WithId<SiteError> }) => (
  <VStack
    align="stretch"
    spacing={3}
    borderWidth="1px"
    borderRadius="lg"
    bg="white"
    p={4}
    boxShadow="sm">
    <HStack justify="space-between" align="flex-start">
      <VStack align="flex-start" spacing={1} flex={1} pr={2}>
        <HStack spacing={2} align="center">
          <Badge colorScheme="red" borderRadius="md">
            Error
          </Badge>
          <Text fontSize="sm" color="gray.600" fontWeight={600}>
            {formatTimestamp(siteError.timestamp)}
          </Text>
        </HStack>
        <HStack spacing={1} align="center">
          <Text fontSize="sm" color="gray.600" fontWeight={500}>
            URL:
          </Text>
          <Tooltip label={siteError.url} placement="top" hasArrow>
            <Link href={siteError.url} isExternal color="blue.600" fontSize="sm">
              <HStack spacing={1}>
                <Text maxW="340px" noOfLines={1}>
                  {siteError.url}
                </Text>
                <Icon as={ExternalLinkIcon} boxSize={3} />
              </HStack>
            </Link>
          </Tooltip>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          User:{' '}
          <Text as="span" fontFamily="mono">
            {siteError.uid ?? 'Anonymous'}
          </Text>
        </Text>
      </VStack>
      <Badge colorScheme="purple" borderRadius="md">
        {siteError.appName}
      </Badge>
    </HStack>
    <Stack spacing={2} divider={<StackDivider borderColor="gray.200" />}>
      <Box>
        <Text fontSize="sm" fontWeight={600} color="gray.700" mb={1}>
          Error Stack
        </Text>
        <Box
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="md"
          bg="gray.50"
          p={3}
          fontFamily="mono"
          fontSize="xs"
          whiteSpace="pre-wrap"
          overflowX="auto">
          {siteError.errorStack}
        </Box>
      </Box>
      <Box>
        <Text fontSize="sm" fontWeight={600} color="gray.700" mb={1}>
          User Agent
        </Text>
        <Text fontSize="xs" color="gray.600" fontFamily="mono">
          {siteError.userAgent}
        </Text>
      </Box>
    </Stack>
  </VStack>
)

const useSiteErrors = () => {
  const { appName } = useApp()
  const authUser = useAuth(s => s.authUser)
  const claims = useAuth(s => s.claims)
  const { selectedPracticeId } = usePracticeAccess()

  const appRole = useMemo(
    () => getAppRole(appName, authUser?.uid ?? null, claims, selectedPracticeId),
    [appName, authUser, claims, selectedPracticeId],
  )
  const errorsQuery = useMemo<UseCollectionArgs<SiteError>>(
    () => ({
      baseQuery: {
        collection: SITE_ERRORS,
        sort: [
          {
            field: 'timestamp',
            order: 'desc',
          },
        ],
      },
      search: '',
      sort: null,
      pause: !(appRole && adminRoles.includes(appRole)),
    }),
    [appRole],
  )

  const data = useListQuery<SiteError>(errorsQuery, PAGE_SIZE)
  return {
    siteErrors: data,
    isAdmin: Boolean(appRole && adminRoles.includes(appRole)),
  }
}

const SiteErrorsPagination = ({
  canGoNext,
  canGoPrev,
  loading,
  onNext,
  onPrev,
  pageNum,
  totalCount,
  totalPages,
}: {
  canGoNext: boolean
  canGoPrev: boolean
  loading: boolean
  onNext: () => void
  onPrev: () => void
  pageNum: number
  totalCount: number
  totalPages: number
}) => {
  if (!totalCount || (totalPages <= 1 && !canGoNext && !canGoPrev)) {
    return null
  }

  const start = Math.min(totalCount, (pageNum - 1) * PAGE_SIZE + 1)
  const end = Math.min(totalCount, pageNum * PAGE_SIZE)

  return (
    <FloatingContainer>
      <HStack
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        spacing={4}
        w="100%"
        flexDir={{ base: 'column', md: 'row' }}>
        <Text fontSize="sm" color="gray.600">
          Showing {start}-{end} of {totalCount} site errors
        </Text>
        <HStack spacing={2} align="center">
          <Button size="sm" variant="outline" onClick={onPrev} isDisabled={!canGoPrev || loading}>
            Previous
          </Button>
          <Text fontSize="sm" color="gray.600">
            Page {pageNum} of {totalPages}
          </Text>
          <Button size="sm" variant="outline" onClick={onNext} isDisabled={!canGoNext || loading}>
            Next
          </Button>
        </HStack>
      </HStack>
    </FloatingContainer>
  )
}

export const SiteErrorsContent = () => {
  const { siteErrors, isAdmin } = useSiteErrors()
  const siteErrorsArr = useArrayFromRecord(siteErrors.items ?? {}) as Array<WithId<SiteError>>
  const totalCount = siteErrors.queryCount ?? siteErrorsArr.length

  if (!isAdmin) {
    return (
      <Center py={20} w="100%">
        <Text color="gray.600">You must be an admin to view site errors.</Text>
      </Center>
    )
  }

  if (siteErrors.loading) {
    return (
      <Center py={20} w="100%" flexDir="column" gap={3}>
        <Spinner color="purple.500" size="lg" thickness="4px" />
        <Text color="gray.600" fontSize="sm">
          Loading site errors...
        </Text>
      </Center>
    )
  }

  if (!siteErrorsArr.length) {
    return (
      <Center py={20} w="100%">
        <Text color="gray.600">No site errors have been recorded.</Text>
      </Center>
    )
  }

  return (
    <VStack align="stretch" spacing={6} w="100%">
      <SiteErrorsPagination
        canGoNext={siteErrors.canGoNext}
        canGoPrev={siteErrors.canGoPrev}
        loading={siteErrors.loading}
        onNext={siteErrors.goNext}
        onPrev={siteErrors.goPrev}
        pageNum={siteErrors.pageNum}
        totalCount={totalCount}
        totalPages={siteErrors.totalPages}
      />
      <VStack align="stretch" spacing={4} w="100%">
        {siteErrorsArr.map(siteError => (
          <SiteErrorCard key={siteError.id} siteError={siteError} />
        ))}
      </VStack>
      <SiteErrorsPagination
        canGoNext={siteErrors.canGoNext}
        canGoPrev={siteErrors.canGoPrev}
        loading={siteErrors.loading}
        onNext={siteErrors.goNext}
        onPrev={siteErrors.goPrev}
        pageNum={siteErrors.pageNum}
        totalCount={totalCount}
        totalPages={siteErrors.totalPages}
      />
    </VStack>
  )
}

export const SiteErrorsView = () => (
  <VStack align="stretch" spacing={6} w="100%">
    <FloatingContainer>
      <Text fontSize="2xl" fontWeight={700} color="gray.700">
        Site Errors
      </Text>
      <Text fontSize="sm" color="gray.600" maxW="720px">
        Review uncaught application errors that have been reported by clients. Use this information
        to investigate and resolve production issues.
      </Text>
    </FloatingContainer>
    <SiteErrorsContent />
  </VStack>
)

export default SiteErrorsView
